<template>
    <base-modal v-if="!geometry" hide-header hide-footer  @close="$emit('close')">
      <h3 class="editor-header">{{ $t('observations.add_new') }}</h3>
      <span v-if="coordinates && coordinates.length > 1"  class="hint-text">{{ $t('observations.line_only_tip') }}</span>
      <div class="col-sm-12 nopads" @click.stop="null">
          <div class="col-sm-12 form-data nopads">
            <div v-if="!observation" style="min-height: 15em">
              <span class="span-title">{{ $t('observations.observation_group') }}</span>
                <select id="group" v-model="group" class="form-select form-select-sm mb-3">
                    <option :value="null">
                        {{ $t('observations.select_group') }}
                    </option>
                    <option v-for="item in groups" :key="item.id" :value="item.id">
                        {{ item.name }}
                    </option>
                </select>
              <span class="span-title">{{ $t('observation_filter.observation_type') }}</span>
              <multiselect
                v-model="type"
                :options="types"
                :close-on-select="true"
                label="name"
                track-by="id"
                :searchable="true"
                :internal-search="true"
                :selected-label="$t('common.selected')"
                :select-label="$t('common.select')"
                :deselect-label="$t('common.remove_selection')"
                :placeholder="$t('observations.select_observation_type')"
                :disabled="!types"
              >
                <template
                  #singleLabel="props"
                >
                  <span class="option__desc">
                    <span class="option__title">{{ props.option.name }}</span>
                  </span>
                </template>
                <template
                  #option="props"
                >
                  <div class="option__desc">
                    <span class="option__title multiselect_detail_block">{{ props.option.name }}</span>
                  </div>
                </template>
                <template #noResult>
                  <span>{{ $t('common.not_found') }}</span>
                </template>
                <template #noOptions>
                  <span>{{ $t('common.no_results') }}</span>
                </template>
              </multiselect>
            </div>

            <div class="col-sm-12 button-container">
              <button class="btn btn-secondary form-button" @click.stop="$emit('close')">
                  {{ $t('common.cancel') }}
              </button>
              <button  class="btn btn-success form-button" :disabled="loading||!type"  @click.stop="submit">
                {{ $t('common.next') }}
              </button>
            </div>
          </div>
      </div>
    </base-modal>

    <base-modal hide-footer :title="$t('observations.add_photo')" v-if="observation && !skipPhotos" @close="$emit('close')">
      <div class="col-sm-12"  @click.stop="null">
        <div class="col-sm-12 editor-form">
          <div class="col-sm-12 form-data">
            <div v-if="observation">
              <!-- Observation photo -->
                <!-- Show selected photos -->
                <div
                    v-for="(file, index) in photos"
                    :key="index"
                    class="d-flex align-items-center justify-content-between mb-3 pb-1"
                    style="border-bottom: #d9d9d9 1px solid"
                >
                    <div class="d-flex align-items-center">
                        <img
                            v-if="showThumbnail && photoPreviewUrl(file)"
                            :src="photoPreviewUrl(file)"
                            :alt="file?.name || ''"
                            class="me-2 rounded border"
                            style="width: 240px; height: 180px; object-fit: contain; background-color: #000;"
                        />
                        <span>{{ file?.name || file }}</span>
                    </div>
                    <button
                        class="btn btn-outline-danger btn-sm"
                        type="button"
                        @click="removePhoto(index)"
                    >
                        {{ $t('common.remove') }}
                    </button>
                </div>

                <!-- Hidden file input -->
                <input
                    ref="photoInput"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    class="visually-hidden"
                    @change="handlePhotoChange"
                />
            </div>
            <div class="col-sm-12 d-flex justify-content-end pt-3">
                <!-- Custom "Add photo" button -->
              <button class="btn btn-primary form-button" type="button" @click="triggerPhotoInput">
                    {{ $t('power_stations.add_photo') }}
              </button>
              <button class="btn btn-secondary form-button" @click.stop="$emit('closeAndUpdate', observation)">
                  {{ $t('common.skip') }}
              </button>
              <button class="btn btn-success form-button" :disabled="loading||photos.length < 1" @click.stop="submit">
                {{ $t('common.save') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </base-modal>
    <div
      v-if="loading"
      id="loader"
      class="spinner"
    />
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import {attachmentHelper} from '../mixins/AttachmentMixin'

import BaseModal from '../BaseModal'
import {inject} from "vue";

export default {
    name: 'ObservationWizard',
    components: {BaseModal},
    mixins: [restApi, attachmentHelper],
    props: {
        coordinates: {
            type: Array,
            default() {
                return []
            }
        },
        observationItem: {
            type: Object,
            default: null
        },
        skipPhotos: {
            type: Boolean,
            default() {
                return false
            }
        },
        contract: {
            type: Number,
            default: null
        },
        contractIds: {
            type: Array,
            default() {
                return []
            }
        },
        initialPhotos: {
            type: Array,
            default() {
                return []
            }
        },
        showThumbnail: {
            type: Boolean,
            default: false
        },
        createdTime: {
            type: String,
            default: null
        }
    },
    data: function () {
        return {
            loading: false,
            groups: [],
            group: null,
            type: null,
            types: [],
            photos: [],
            previewUrls: new Map(),
            observation: null,
            defineGeometryLine: false,
            geometry: null
        }
    },
    emits: ['close', 'closeAndUpdate'],
    watch: {
        group(val) {
            this.types = this.getObsTypesByGroup(val)
        },
        observationItem() {
            this.observation = this.observationItem
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    mounted: function () {
        if (this.observationItem) {
            this.observation = this.observationItem
            this.group = this.observationItem.type.observation_group
            this.type = this.observationItem.type
        }
        if (this.initialPhotos && this.initialPhotos.length > 0) {
            this.photos.push(...this.initialPhotos)
        }
        this.fetchObsGroups()
        this.fetchAllObsTypes()
    },
    beforeUnmount() {
        this.previewUrls.forEach((url) => URL.revokeObjectURL(url))
        this.previewUrls.clear()
    },
    methods: {
        triggerPhotoInput() {
            this.$refs.photoInput?.click();
        },
        handlePhotoChange(event) {
            const file = event.target.files[0];
            if (file) {
                this.photos.push(file);
            }
            // Clear input value so the same file can be selected again if needed
            event.target.value = '';
        },
        removePhoto(index) {
            const file = this.photos[index]
            if (file && this.previewUrls.has(file)) {
                URL.revokeObjectURL(this.previewUrls.get(file))
                this.previewUrls.delete(file)
            }
            this.photos.splice(index, 1);
        },
        // Returns a cached object URL for previewing an image File. Non-image
        // entries (or already-uploaded photos represented as strings) return
        // null so no <img> is rendered. URLs are revoked on remove/unmount.
        photoPreviewUrl(file) {
            if (!(file instanceof Blob) || !file.type || !file.type.startsWith('image/')) {
                return null
            }
            if (!this.previewUrls.has(file)) {
                this.previewUrls.set(file, URL.createObjectURL(file))
            }
            return this.previewUrls.get(file)
        },
        fetchObsGroups: function () {
            this.group = null
            this.groups = []
            this.loading = true
            this.restFetch(this.observationGroupUrl, this.handleGroupResponse)
        },
        handleGroupResponse: function (response) {
            this.groups = response.data
            this.loading = false
        },
        fetchAllObsTypes: function () {
            this.type = null
            this.allTypes = []
            this.loading = true
            this.restFetch(this.observationTypeUrl, this.handleAllTypeResponse)
        },
        getObsTypesByGroup(group) {
            let filteredTypes = []
            if(this.allTypes && this.allTypes.length > 0) {
                if(group) {
                    this.allTypes.forEach(type => {
                        if(type.observation_group.id === group) {
                            filteredTypes.push(type)
                        }
                    })
                } else {
                    filteredTypes = this.allTypes
                }
            }
            return filteredTypes
        },
        handleAllTypeResponse: function (response) {
            if (this.coordinates.length === 1) {
                // Point
                this.allTypes = response.data
            } else {
                this.allTypes = response.data.filter(item => item.geometry_type !== 1)  // Line
            }
            this.types = this.allTypes
            this.loading = false
        },
        submit: function () {
            if (!this.observation) {
                if (this.type.geometry_type === 1 || (this.coordinates && this.coordinates.length > 1)) {
                    if(this.coordinates.length === 1) {
                        this.geometry = {'point': {'lat': this.coordinates[0].lat, 'lon': this.coordinates[0].lng}}
                    } else {
                        this.geometry = {
                            'line_string': {
                                'points': this.coordinates.map(item => { return [item.lng, item.lat] })
                            }
                        }
                    }
                    this.defineGeometryLine = false
                    this.submitObservation()
                } else {
                    // line without given coordinates
                    if (this.line) {
                        this.defineGeometryLine = false
                        this.submitObservation()
                    } else {
                        // ask for line
                        this.defineGeometryLine = true
                        this.geometry = {'line_string': {'points': [[this.coordinates[0].lng, this.coordinates[0].lat]]}}
                        this.$emit('lineRequest', this.getObservation())
                    }
                }
            } else {
                this.uploadPhoto()
            }
        },
        getObservation: function() {
            let params = {}
            if (this.type) {
                params.type = {'id': this.type.id}
            }
            params.geometry = this.geometry
            params.bearing = 0
            params.road_direction = 1
            params.road_lane = 1
            params.created_time = this.createdTime || new Date().toISOString()
            if(this.contractIds && this.contractIds.length === 1) {
                params.contract = {id: this.contractIds[0]}
            } else if(this.contract) {
                params.contract = {id: this.contract}
            } else {
                params.contract = null
            }
            return params
        },
        submitObservation: function () {
            this.restAdd(this.observationUrl, this.getObservation(), this.success, this.fail)
        },
        success: function (response) {
            this.loading = false
            this.observation = response
            if (this.skipPhotos) {
                this.$emit('closeAndUpdate', this.observation)
            }
        },
        fail: function () {
            this.loading = false
            this.$emit('close')
            this.showAlert(this.$t('common.alert_add_failed'))
        },
        uploadPhoto: function () {
            var validPhotos = []
            for (let i = 0; i < this.photos.length; i++) {
                if (this.photos[i] !== undefined && this.photos[i] !== null) {
                    validPhotos.push(this.photos[i])
                }
            }
            if (validPhotos.length > 0) {
                this.loading = true
                let formData = new FormData()
                formData.append('observation_id', this.observation.id)
                validPhotos.forEach(photoFile => {
                    if (photoFile !== undefined && photoFile !== null) {
                        formData.append('photos[]', photoFile)
                    }
                })
                this.restPostWithFile(this.observationPhotoUrl, {}, formData, this.photoSuccess, this.photoFail
                )
            } else {
                this.showAlert(this.$t('observations.select_photo'))
            }
        },
        photoSuccess: function () {
            this.loading = false
            this.$emit('closeAndUpdate', this.observation)
        },
        photoFail: function () {
            this.loading = false
            this.showAlert(this.$t('common.alert_update_failed'))
        }
    }
}
</script>
<style>
@import "../../styles/modal.css";
</style>
